import Link from 'next/link';
import { ListCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { HOME3_CANVAS_LABEL_KEY, type Home3Destination, type Home3ReadState } from '../../../lib/home3-feed';
import { useBasePath } from '../../../lib/use-base-path';
import { useTranslation } from '../../../lib/i18n';
import { Typography } from '../ui';
import { HOME3_CATEGORY_ORDER, Home3CategoryArt, type Home3Messenger } from './Home3Categories';
import styles from './Home3EntryPoints.module.css';

/** What an entry point stands for: the brief, or one destination. */
export type Home3EntryPoint = 'brief' | Home3Destination;

/** Top to bottom, as the frame stacks them: the brief, then the categories in the board's own order. */
export const HOME3_ENTRY_POINTS: readonly Home3EntryPoint[] = ['brief', ...HOME3_CATEGORY_ORDER];

/**
 * Where each category's entry point goes — the page the Agents screen's tile for it opens (`TasksSummary`,
 * `RoutinesSummary`, `GoalsSummary`, `ContactsSummaryCard`, `AppsSummaryCard`, `FilesSummaryCard`), unscoped,
 * so the two doors into a category land on the same screen. Messages is the outreach ledger, `/contacts`.
 */
export const HOME3_ENTRY_ROUTE: Record<Home3Destination, string> = {
  tasks: '/tasks',
  routines: '/routines',
  goals: '/goals',
  outreach: '/contacts',
  apps: '/apps',
  files: '/files',
};

/**
 * The canvas's ENTRY POINTS — the brief and the six categories, stacked in a column docked at the canvas's
 * inline end, its end edge on the header's (Figma 2045:104584). They replace the map's outreach column.
 *
 * ## They are SCREEN furniture, not part of the map
 *
 * The column is HTML beside the map's `<svg>`, never inside it, so the camera cannot reach it: panning
 * and zooming leave every entry point's size, shape and position exactly where they are. The lines into
 * them are the part that moves — drawn from a world point on the map to the entry point's SCREEN point, so
 * they stretch and shrink with the camera while the end they land on stays put. Each art box carries
 * `data-entry-point` for that: it is the element whose rect a line ends on.
 *
 * The column sits in `.canvasDock` (`Home3Body`), whose end edge is the header's, so the two line up.
 *
 * ## Doors, not a filter
 *
 * A category's entry point is a LINK to that category's page (`HOME3_ENTRY_ROUTE`), so a modifier click opens it
 * in a new tab like any other link; `onOpen` runs on the way out (BI, and clearing the category's badge).
 * The brief is a button — it opens the brief where it is rather than leaving the home, and does nothing
 * until `onBrief` is given. Nothing stays chosen, so the white plate is the hover only.
 *
 * ## The art is the board tiles' own
 *
 * Each category draws `Home3CategoryArt` — the very mark its tile in the task-list view wears, Messages'
 * live face included — so the home's two views of a category cannot drift apart. Every mark sits in a 40
 * box. The category labels are the board's keys. The brief's mark is a token tile under lucide's
 * `ListCheck` — the frame's own `24/list-check` is that glyph — so it flips in dark mode.
 *
 * ## The count badges are the activity panel's
 *
 * A category's badge is how many of the rows the canvas's player replays this loop are that category's
 * (`Home3Body`'s `entryCounts`) — the panel's own rows, so the six add up to its headline. Drawn only once
 * the feed is `ready`, never on the brief, and still while it plays. The frame draws no badge, so it is the
 * board tile's disc (Figma 20077:39153), seated on the art box's top-end corner.
 */
export function Home3EntryPoints({
  unread,
  readState,
  messenger,
  onOpen,
  onBrief,
}: {
  /** The count each category wears — on the canvas, what the player's loop replays in it. Zero means no badge. */
  unread: Record<Home3Destination, number>;
  /** Whoever wrote most recently — Messages wears their face, as the board's tile does. */
  messenger?: Home3Messenger;
  /** Until `ready` no badge is drawn: a count of 0 and "not known yet" are different facts. */
  readState: Home3ReadState;
  /** A category's entry point was pressed; the link navigates by itself. */
  onOpen: (entry: Home3Destination) => void;
  /** The brief's entry point was pressed. Absent, it does nothing. */
  onBrief?: () => void;
}) {
  const { t } = useTranslation();
  const basePath = useBasePath();

  const body = (entry: Home3EntryPoint, art: ReactNode, label: string) => {
    const count = entry !== 'brief' && readState === 'ready' ? unread[entry] : 0;
    return (
      <>
        <span className={styles.art} data-entry-point={entry}>
          {art}
          {count > 0 && (
            // Figma's Super Tiny (10/16) — a DS role the `Typography` primitive has no variant for, so it is its class.
            <span className={`sym-type-super-tiny-semi-bold ${styles.badge}`}>{count}</span>
          )}
        </span>
        {/* Figma's Small/Semi Bold — the legacy scale's `h4` is the same 14/18 at the same weights. */}
        <Typography variant="h4" color="primary" component="span" className={styles.label} noWrap>
          {label}
        </Typography>
      </>
    );
  };

  return (
    <div className={styles.column} data-entry-column>
      {/* `data-brief-toggle`: a press here is the popover's own toggle, not a press outside it (`Home3BriefPopover`). */}
      <button type="button" className={styles.entry} onClick={onBrief} data-brief-toggle>
        {body(
          'brief',
          <span className={styles.briefTile} aria-hidden>
            <ListCheck size={22.3} strokeWidth={1.8} absoluteStrokeWidth className={styles.briefGlyph} />
          </span>,
          t('home.overview.entry.brief'),
        )}
      </button>
      {HOME3_CATEGORY_ORDER.map((entry) => (
          <Link
            key={entry}
            className={`${styles.entry}${entry === 'outreach' ? ` ${styles.entryMessages}` : ''}${
              entry === 'files' ? ` ${styles.entryFiles}` : ''
            }`}
            // The bare route as `href`, the mount-prefixed URL as `as` — see `routeLink`.
            href={HOME3_ENTRY_ROUTE[entry]}
            as={`${basePath}${HOME3_ENTRY_ROUTE[entry]}`}
            onClick={() => onOpen(entry)}
          >
            {body(entry, <Home3CategoryArt destination={entry} messenger={messenger} />, t(HOME3_CANVAS_LABEL_KEY[entry]))}
          </Link>
      ))}
    </div>
  );
}
